/**
 * `creation_log` — small avatar of `creator_id` + the date, per `mode` (M2 §5.11, §6).
 * Never writable, never empty.
 */
import { getColumnType, type CreationLogSettings, type CreationLogValue } from '@elizmonday/shared';
import { PersonAvatar, UnknownAvatar } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { CreationLogSettingsDialog } from './SettingsDialog';

const shared = getColumnType('creation_log');

/*
 * The settings type is `packages/shared`'s own (round 10i): this folder used to declare a local
 * `{mode?, dateFormatMode?}` stand-in, whose `string` members are *wider* than the shared enums,
 * so a `SettingsDialog` typed against `CreationLogSettings` could not be registered here.
 */
/** `2026-09-09T17:44:52Z` -> `Sep 9, 2026`, in the viewer's timezone. */
export function formatLogDate(iso: string | undefined, timezone: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  }).format(date);
}

export const creationLogColumnUi = defineColumnUi<CreationLogValue, CreationLogSettings>({
  type: 'creation_log',
  icon: 'columnCreationLog',
  gallery: { group: 'dates', keywords: ['created', 'creation log', 'author'] },
  /** P-022: `174316936` `Requester` (`width: null`) renders **135** px in monday. */
  defaultWidth: 135,
  align: 'center',
  groupable: false,

  Cell: ({ value, settings, context }) => {
    if (!value) return <span className="ez-cell__empty" aria-hidden="true" />;
    const person = context.people.find((entry) => String(entry.id) === String(value.creator_id));
    /*
     * monday's `mode` vocabulary is `personAndDate` | `personOnly` | `dateOnly`
     * (`creationLogSettingsSchema`). This read `'date'` and `'person'` — two values the store
     * never holds — so **both branches were dead** and the cell always drew avatar + date
     * whatever the column was set to. Found by typing the definition against the shared settings
     * when the settings dialog landed (round 10i); the local `{mode?: string}` stand-in had been
     * wide enough to hide it.
     */
    const mode = settings.mode ?? 'personAndDate';
    return (
      <span className="ez-log-cell">
        {mode === 'dateOnly' ? null : person ? (
          <PersonAvatar name={person.name} color={person.color} size={20} />
        ) : (
          <UnknownAvatar size={20} />
        )}
        {mode === 'personOnly' ? null : formatLogDate(value.created_at, context.timezone)}
      </span>
    );
  },

  SettingsDialog: CreationLogSettingsDialog,

  fromClipboard() {
    throw new Error('read-only');
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('creation_log'),
});
